import { API, authFetch } from "@/App";
import { toast } from "sonner";

const ORDER_SYNC_CHANNEL = "orderflow_order_sync";

export function useOrderActions({
  extractedData, setExtractedData, editedData, setEditedData,
  isEditing, setIsEditing, currentOrderId, setCurrentOrderId,
  setIsExporting, emailContent, setEmailContent,
  collectedEmails, setCollectedEmails, detectedCompany,
  setConflictsModal, resetChat, setDetectedCompany,
  navigate,
}) {
  // Export / Save to Tableau
  const handleExport = async () => {
    if (!extractedData && !editedData) {
      toast.error("Aucune donnée à enregistrer");
      return;
    }
    setIsExporting(true);
    try {
      const dataToSave = editedData || extractedData;
      let orderId = currentOrderId;

      if (!orderId) {
        const rawEmailCombined = collectedEmails.map((e, idx) =>
          idx === 0 ? e.content : `--- EMAIL COMPLÉMENTAIRE ---\n\n${e.content}`
        ).join('\n\n');

        const createResponse = await authFetch(`${API}/orders/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            extracted_data: dataToSave, raw_email: rawEmailCombined,
            emails: collectedEmails, detected_company: detectedCompany,
          }),
        });
        if (createResponse.ok) {
          const createData = await createResponse.json();
          orderId = createData.order_id;
          setCurrentOrderId(orderId);
        } else {
          const error = await createResponse.json();
          toast.error(error.detail || "Erreur lors de la création");
          setIsExporting(false);
          return;
        }
      } else {
        await authFetch(`${API}/orders/${orderId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ order_id: orderId, extracted_data: dataToSave }),
        });
      }

      setExtractedData(dataToSave);
      setIsEditing(false);

      const channel = new BroadcastChannel(ORDER_SYNC_CHANNEL);
      channel.postMessage({
        type: 'NEW_ORDER',
        order: { order_id: orderId, extracted_data: dataToSave, created_at: new Date().toISOString() },
      });
      channel.close();

      try {
        await authFetch(`${API}/modified-rows/${orderId}`, { method: "POST" });
      } catch (e) {
        console.error("Failed to mark row as modified:", e);
      }

      toast.success("Commande enregistrée !");
      navigate("/tableau");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Erreur lors de l'enregistrement: " + error.message);
    } finally {
      setIsExporting(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setIsEditing(false);
      setEditedData({ ...extractedData });
    } else {
      setIsEditing(true);
      setEditedData({ ...extractedData });
    }
  };

  const handleSaveEdits = async () => {
    if (!currentOrderId || !editedData) return;
    try {
      const response = await authFetch(`${API}/orders/${currentOrderId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ order_id: currentOrderId, extracted_data: editedData }),
      });
      if (response.ok) {
        setExtractedData(editedData);
        setIsEditing(false);
        toast.success("Modifications enregistrées");
      } else {
        toast.error("Erreur lors de la sauvegarde");
      }
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleDataChange = (field, value) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  const handleClearEmailZone = () => {
    setEmailContent("");
    toast.success("Zone de texte effacée - Données conservées");
  };

  const handleClearAll = () => {
    setEmailContent("");
    setExtractedData(null);
    setEditedData(null);
    setCurrentOrderId(null);
    setDetectedCompany(null);
    resetChat();
    setCollectedEmails([]);
    setConflictsModal({ open: false, conflicts: {}, resolved: {} });
    toast.success("Tout a été réinitialisé");
  };

  return {
    handleExport, handleEditToggle, handleSaveEdits, handleDataChange,
    handleClearEmailZone, handleClearAll,
  };
}
